import React, { useEffect, useRef, useState } from 'react'
import { Loading } from 'src/components/common/Loading'
import RenderIf from 'src/components/common/RenderIf'
import PassPageView from 'src/pages/PassPage/view'
import {
  passDataEncryptRequestQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { PassDataEncryptRequestSpace, ResponseDataType } from 'src/types/api'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import useParamsType from 'src/hooks/useParamsType.hook'
import { useModal } from 'src/provider/ModalContextProvider'
import usePageNav from 'src/hooks/usePageNav.hook'
import useLocalStorage from 'src/hooks/useLocalStorage.hook'
import { useCookies } from 'react-cookie'

export interface PassPageProps {
  requestPCCFormRef: React.RefObject<HTMLFormElement>
  requestPCCForm: PassDataEncryptRequestSpace.Response
  passReqNum: string
  showIosPage: boolean
}

const PassPage = () => {
  const { openModal } = useModal()
  const { passReqNum, passReqType } = useParamsType()
  const { pass_channel } = useParamsType()
  const { navigatePrev } = usePageNav()
  const [, setPassWindowHistoryPoint] = useLocalStorage<number>('pass_window_history_point', 0)
  const [cookies] = useCookies(['req_num'])
  const [requestPCCForm, setRequestPCCForm] = useState({
    CryptoTokenID: '',
    IntegrityValue: '',
    ReqInfo: '',
    Version: '',
  })
  const [showIosPage, setShowIosPage] = useState(false)
  const requestPCCFormRef = useRef<HTMLFormElement>(null)

  const aos = pass_channel === 'aos'
  const internalOpen = !!cookies['req_num'] || aos

  const { mutateAsync: passDataEncryptRequestMutate, isPending: passDataEncryptPending } =
    useCustomMutation<PassDataEncryptRequestSpace.Request, PassDataEncryptRequestSpace.Response>(
      passDataEncryptRequestQueryOption(),
    )

  useEffect(() => {
    if (passReqType === '-1') {
      navigatePrev()
    } else if (!!passReqNum) {
      passDataEncryptRequestMutate({ ReqNum: passReqNum }).then(
        (res: ResponseDataType<PassDataEncryptRequestSpace.Response>) => {
          const { ErrCode, ErrMsg } = res.Header
          if (ErrCode !== ERRCODE_SUCCESS) {
            openModal({
              contents: { t: '일시적인 접속 오류', d: ErrMsg },
              button: { submit: { t: '닫기', f: handleCloseButton } },
            })
          } else if (!res.Body.CryptoTokenID) {
            openModal({
              contents: { d: '올바른 요청데이터가 아닙니다.' },
              button: { submit: { t: '닫기', f: handleCloseButton } },
            })
          } else {
            setRequestPCCForm({ ...res.Body })
          }
        },
      )
    }

    // 뒤로가기 캐시 유지로 인한 미작동 방지
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted && window.location.pathname.includes('pass/-1/0')) {
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Step2: 암호화된 데이터로 PASS 화면 요청
  useEffect(() => {
    if (!!requestPCCForm?.CryptoTokenID) {
      internalOpen && setPassWindowHistoryPoint(window.history.length)
      requestPCCFormRef.current?.submit()
      internalOpen && window.history.replaceState(null, '', `/${pass_channel}/pass/-1/0`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPCCForm])

  const handleCloseButton = () => {
    if (internalOpen) {
      navigatePrev()
    } else {
      switch (pass_channel) {
        case 'ios':
          window.location.href = 'couponwalletapp://payment_return'
          setShowIosPage(true)
          break
        default:
          window.open('', '_self', '')
          window.close()
          break
      }
    }
  }

  const props: PassPageProps = {
    requestPCCFormRef,
    requestPCCForm,
    passReqNum,
    showIosPage,
  }

  return (
    <>
      <RenderIf when={passDataEncryptPending}>
        <Loading.Default />
      </RenderIf>
      <PassPageView {...props} />
    </>
  )
}

export default PassPage
